import axios from "axios";
import authedAxios from "./axios";

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

/**
 * GET /microsoft/oauth/authorize_url?intent= — URL generated on API (client secret stays server-side).
 * @param {'company_inbox' | 'user_mail' | 'login' | 'company_ai_mailbox'} intent
 * @param {Record<string, string|number>} [extraParams] e.g. { company_ai_mailbox_id: 12 }
 */
export function getMicrosoftAuthorizeUrl(intent, extraParams = {}) {
  const client = intent === "login" ? publicApi : authedAxios;
  return client
    .get("/microsoft/oauth/authorize_url", {
      params: { intent, ...extraParams },
    })
    .then((r) => r.data);
}

/**
 * POST /microsoft/oauth/token — exchange code (Bearer except login flow uses public client).
 * @param {{ code: string, state: string }} payload
 */
export function postMicrosoftOAuthExchange(payload, { isLoginFlow } = {}) {
  const client = isLoginFlow ? publicApi : authedAxios;
  return client
    .post("/microsoft/oauth/token", payload)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** @deprecated use postMicrosoftOAuthExchange */
export function postMicrosoftOAuthToken(payload) {
  return postMicrosoftOAuthExchange(payload, { isLoginFlow: false });
}

/** @deprecated use postMicrosoftOAuthExchange */
export function postMicrosoftOAuthLogin(payload) {
  return postMicrosoftOAuthExchange(payload, { isLoginFlow: true });
}

export function fetchCompanyMicrosoftStatus() {
  return authedAxios
    .get("/microsoft/company/status")
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

export function disconnectCompanyMicrosoft() {
  return authedAxios
    .delete("/microsoft/company/disconnect")
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

export function fetchUserMicrosoftStatus() {
  return authedAxios
    .get("/microsoft/me/status")
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

export function disconnectUserMicrosoft() {
  return authedAxios
    .delete("/microsoft/me/disconnect")
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** GET /microsoft/me/calendars — delegated token; returns { calendars: [...] }. */
export function fetchUserMicrosoftCalendars() {
  return authedAxios
    .get("/microsoft/me/calendars")
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/**
 * PATCH /microsoft/me/calendar — save calendar used for registering events.
 * @param {{ event_calendar_id?: string, event_calendar_name?: string, event_calendar_owner_email?: string }} payload
 */
export function patchUserMicrosoftCalendarSelection(payload) {
  return authedAxios
    .patch("/microsoft/me/calendar", payload)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** POST /microsoft/me/send_test_mail — sends a test message to the linked mailbox address. */
export function postMicrosoftSendTestMail() {
  return authedAxios
    .post("/microsoft/me/send_test_mail")
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** GET /microsoft/company/ai_mailboxes — admin; list addresses for AI ingestion. */
export function fetchCompanyAiMailboxes() {
  return authedAxios
    .get("/microsoft/company/ai_mailboxes")
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** POST /microsoft/company/ai_mailboxes — admin; body: { email_address }. */
export function createCompanyAiMailbox(payload) {
  return authedAxios
    .post("/microsoft/company/ai_mailboxes", payload)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** PATCH /microsoft/company/ai_mailboxes/:id — admin; mail processing settings. */
export function patchCompanyAiMailbox(id, payload) {
  return authedAxios
    .patch(`/microsoft/company/ai_mailboxes/${id}`, payload)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/**
 * POST /microsoft/company/ai_mailboxes/:id/process_messages
 * @param {number|string} id
 * @param {{ graph_message_ids: string[], dry_run?: boolean }} payload — dry_run defaults true on API if omitted
 */
export function postCompanyAiMailboxProcessMessages(id, payload) {
  return authedAxios
    .post(`/microsoft/company/ai_mailboxes/${id}/process_messages`, payload)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** DELETE /microsoft/company/ai_mailboxes/:id — admin. */
export function deleteCompanyAiMailbox(id) {
  return authedAxios
    .delete(`/microsoft/company/ai_mailboxes/${id}`)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** GET /microsoft/company/ai_mailboxes/:id/processing_logs?graph_message_id=&internet_message_id=&limit= */
export function fetchCompanyAiMailboxProcessingLogs(
  id,
  { graphMessageId, internetMessageId, limit = 20 } = {}
) {
  const params = { graph_message_id: graphMessageId, limit };
  if (internetMessageId) params.internet_message_id = internetMessageId;
  return authedAxios
    .get(`/microsoft/company/ai_mailboxes/${id}/processing_logs`, {
      params,
    })
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** GET /microsoft/company/ai_mailboxes/:id/message?graph_message_id= — full body + recipients (admin). */
export function fetchCompanyAiMailboxMessage(id, graphMessageId) {
  return authedAxios
    .get(`/microsoft/company/ai_mailboxes/${id}/message`, {
      params: { graph_message_id: graphMessageId },
    })
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** GET /microsoft/company/ai_mailboxes/:id/reply_draft_message?graph_message_id= — AI reply draft (inbound id). */
export function fetchCompanyAiMailboxReplyDraftMessage(id, inboundGraphMessageId) {
  return authedAxios
    .get(`/microsoft/company/ai_mailboxes/${id}/reply_draft_message`, {
      params: { graph_message_id: inboundGraphMessageId },
    })
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** PATCH /microsoft/company/ai_mailboxes/:id/reply_draft_message — { graph_message_id, body_html } */
export function patchCompanyAiMailboxReplyDraftMessage(id, payload) {
  return authedAxios
    .patch(`/microsoft/company/ai_mailboxes/${id}/reply_draft_message`, payload)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/**
 * GET /microsoft/company/ai_mailboxes/:id/messages?top=&mail_folder_id=&well_known_folder=
 * Use either mailFolderId (Graph id) or wellKnownFolder (inbox, archive, …), not both.
 */
export function fetchCompanyAiMailboxMessages(
  id,
  { top = 20, mailFolderId, wellKnownFolder } = {}
) {
  const params = { top };
  if (mailFolderId) params.mail_folder_id = mailFolderId;
  if (wellKnownFolder) params.well_known_folder = wellKnownFolder;
  return authedAxios
    .get(`/microsoft/company/ai_mailboxes/${id}/messages`, { params })
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** GET /microsoft/company/ai_mailboxes/:id/mail_folders */
export function fetchCompanyAiMailboxMailFolders(id) {
  return authedAxios
    .get(`/microsoft/company/ai_mailboxes/${id}/mail_folders`)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** POST /microsoft/company/ai_mailboxes/:id/send_test_mail */
export function postCompanyAiMailboxSendTestMail(id) {
  return authedAxios
    .post(`/microsoft/company/ai_mailboxes/${id}/send_test_mail`)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** DELETE /microsoft/company/ai_mailboxes/:id/microsoft — remove stored Microsoft tokens */
export function disconnectCompanyAiMailboxMicrosoft(id) {
  return authedAxios
    .delete(`/microsoft/company/ai_mailboxes/${id}/microsoft`)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** GET /microsoft/me/mail_ai/settings — admin or microsoft_personal_mail_ai role */
export function fetchUserPersonalMailAiSettings() {
  return authedAxios
    .get("/microsoft/me/mail_ai/settings")
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** PATCH /microsoft/me/mail_ai/settings */
export function patchUserPersonalMailAiSettings(payload) {
  return authedAxios
    .patch("/microsoft/me/mail_ai/settings", payload)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** GET /microsoft/me/mail_ai/messages */
export function fetchUserPersonalMailAiMessages({
  top = 20,
  mailFolderId,
  wellKnownFolder,
} = {}) {
  const params = { top };
  if (mailFolderId) params.mail_folder_id = mailFolderId;
  if (wellKnownFolder) params.well_known_folder = wellKnownFolder;
  return authedAxios
    .get("/microsoft/me/mail_ai/messages", { params })
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** GET /microsoft/me/mail_ai/mail_folders */
export function fetchUserPersonalMailAiMailFolders() {
  return authedAxios
    .get("/microsoft/me/mail_ai/mail_folders")
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** GET /microsoft/me/mail_ai/message?graph_message_id= */
export function fetchUserPersonalMailAiMessage(graphMessageId) {
  return authedAxios
    .get("/microsoft/me/mail_ai/message", {
      params: { graph_message_id: graphMessageId },
    })
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** GET /microsoft/me/mail_ai/reply_draft_message?graph_message_id= */
export function fetchUserPersonalMailAiReplyDraftMessage(inboundGraphMessageId) {
  return authedAxios
    .get("/microsoft/me/mail_ai/reply_draft_message", {
      params: { graph_message_id: inboundGraphMessageId },
    })
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** PATCH /microsoft/me/mail_ai/reply_draft_message */
export function patchUserPersonalMailAiReplyDraftMessage(payload) {
  return authedAxios
    .patch("/microsoft/me/mail_ai/reply_draft_message", payload)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/**
 * POST /microsoft/me/mail_ai/process_messages
 * @param {{ graph_message_ids: string[], dry_run?: boolean }} payload
 */
export function postUserPersonalMailAiProcessMessages(payload) {
  return authedAxios
    .post("/microsoft/me/mail_ai/process_messages", payload)
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}

/** GET /microsoft/me/mail_ai/processing_logs */
export function fetchUserPersonalMailAiProcessingLogs({
  graphMessageId,
  internetMessageId,
  limit = 20,
} = {}) {
  const params = { graph_message_id: graphMessageId, limit };
  if (internetMessageId) params.internet_message_id = internetMessageId;
  return authedAxios
    .get("/microsoft/me/mail_ai/processing_logs", { params })
    .then((r) => r.data)
    .catch((err) => {
      throw err;
    });
}
